import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseTabs from 'components/atoms/base-tabs';
import { ModuleType } from 'infrastructure/enums';
import BaseLoader from 'components/atoms/base-loader';
import BaseButton from 'components/atoms/baseButton';
import KeyNotesContent from 'components/organisms/admin-panel-key-notes/key-notes-content';
import BaseCheckbox from 'components/atoms/base-checkbox';
import PlusIcon from 'components/atoms/icons/plusIcon';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { CreateOrUpdateKeyNoteModal } from 'components/organisms/admin-panel-key-notes/modal/create-or-update-key-note';
import { useGetKeyNote, useSaveKeyNote } from 'infrastructure/hooks/key-notes';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';

import s from './styles.module.scss';

import type { IBaseTab } from 'components/atoms/base-tabs';

interface IProps {
  patientGuid: string;
}

interface ISaveProps {
  note: string;
  expiresAt?: Date | null;
}

const CallDashboardKeyNotesForm: React.FC<IProps> = (props) => {
  const { patientGuid } = props;

  const { t } = useTranslation();

  const { isReadOnlyRole } = useCheckRoleAccess();

  const [showExpired, setShowExpired] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const tabs: IBaseTab<ModuleType.RPM | ModuleType.CCM>[] = [
    {
      key: ModuleType.RPM,
      label: t('labels.rpm'),
    },
    {
      key: ModuleType.CCM,
      label: t('labels.ccm'),
    },
  ];

  const [tab, setTab] = useState(tabs[0]);

  const { saveNote, isLoading } = useSaveKeyNote({
    module: tab.key,
  });

  const [
    { data: rpmData, isFetching: rpmLoading },
    { data: ccmData, isFetching: ccmLoading },
  ] = useGetKeyNote({
    module: tab.key,
    patientGuid,
    canChangeModule: true,
    showExpired,
  });

  const data = tab.key === ModuleType.RPM ? rpmData : ccmData;
  const loading = rpmLoading || ccmLoading;

  const handleSave = ({ note, expiresAt }: ISaveProps) => {
    saveNote(
      {
        patientGuid,
        module: tab.key,
        note,
        expiresAt: expiresAt || null,
        showExpired,
      },
      {
        onSuccess() {
          setIsEdit(false);
          notificationController.success({
            message: t('messages.keynoteAdded'),
          });
        },
      },
    );
  };

  return (
    <div>
      <CreateOrUpdateKeyNoteModal
        onCancel={() => setIsEdit(false)}
        moduleType={tab.key}
        isOpen={isEdit}
        isLoading={isLoading}
        onSave={(values) => {
          handleSave(values);
        }}
      />
      <div className={s.head}>
        <span className={s.title}>{t('labels.keyNotes')}</span>
        <div className={s.controls}>
          <BaseTabs tabs={tabs} size="small" type="rounded" onChange={setTab} />
          <BaseButton
            type="secondary"
            label={t('labels.showExpiredEntries')}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowExpired(!showExpired);
            }}
            disabled={isReadOnlyRole || loading}
            className={s['show-expired']}
            icon={
              <BaseCheckbox value={showExpired} onChange={setShowExpired} />
            }
          />
          <BaseButton
            type="primary"
            label={t('controls.add')}
            onClick={() => {
              setIsEdit(true);
            }}
            disabled={isReadOnlyRole || loading}
            icon={<PlusIcon />}
            className={s['add-btn']}
          />
        </div>
      </div>

      <BaseLoader loading={loading}>
        <KeyNotesContent
          patientGuid={patientGuid}
          module={tab.key}
          isDataEmpty={!data?.length}
          data={data}
          showExpired={showExpired}
        />
      </BaseLoader>
    </div>
  );
};

export default CallDashboardKeyNotesForm;
